import {
  DeleteOutlined,
  DownloadOutlined,
  PaperClipOutlined,
} from '@ant-design/icons';
import { Button, Modal, Space, Spin, Tooltip, Typography, Upload } from 'antd';
import { useMemo } from 'react';

import type { ModelClientFile } from '@app/api/ClientFiles/types';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';

import {
  isAllowedFileExtension,
  isAllowedFileSize,
  LEAD_CARD_FILE_ACCEPTED_TYPES,
} from '../../context/hooks/useAddFile';

import type { RcFile, UploadProps } from 'antd/es/upload/interface';
import type { FC } from 'react';

import './AddFileModal.scss';

const { Text } = Typography;
const { Dragger } = Upload;

interface AddFileModalProps {
  isOpen: boolean;
  files: ModelClientFile[];
  isFilesLoading: boolean;
  isUploadingFile: boolean;
  isRemovingFile: boolean;
  isDownloadingFile: boolean;
  onAttachFile(file: RcFile): Promise<void>;
  onDownloadFile(file: ModelClientFile): Promise<void>;
  onRemoveFile(fileId: number): Promise<void>;
  onClose(): void;
}

const HELPER_TEXT =
  'Максимальный размер файла — 300 МБ. Допустимые форматы: xlsx, xls, jpg, png, jpeg, pdf, doc, docx, txt, mp4, webp.';
const INVALID_FORMAT_MESSAGE =
  'Недопустимый формат файла. Разрешены: xlsx, xls, jpg, png, jpeg, pdf, doc, docx, txt, mp4, webp';
const INVALID_SIZE_MESSAGE = 'Максимальный размер файла — 300 МБ';

export const AddFileModal: FC<AddFileModalProps> = ({
  isOpen,
  files,
  isFilesLoading,
  isUploadingFile,
  isRemovingFile,
  isDownloadingFile,
  onAttachFile,
  onDownloadFile,
  onRemoveFile,
  onClose,
}) => {
  const { showNotification } = useNotification();
  const isBusy =
    isFilesLoading || isUploadingFile || isRemovingFile || isDownloadingFile;

  const uploadProps: UploadProps = useMemo(
    () => ({
      name: 'file',
      multiple: true,
      accept: LEAD_CARD_FILE_ACCEPTED_TYPES,
      showUploadList: false,
      beforeUpload: (file) => {
        if (!isAllowedFileExtension(file.name)) {
          showNotification(INVALID_FORMAT_MESSAGE, 'frontWarning');
          return Upload.LIST_IGNORE;
        }

        if (!isAllowedFileSize(file.size)) {
          showNotification(INVALID_SIZE_MESSAGE, 'frontWarning');
          return Upload.LIST_IGNORE;
        }

        void onAttachFile(file as RcFile);
        return false;
      },
      disabled: isUploadingFile || isRemovingFile,
    }),
    [isRemovingFile, isUploadingFile, onAttachFile, showNotification]
  );

  return (
    <Modal
      title="Прикрепить файл или фото"
      okText="Закрыть"
      open={isOpen}
      onCancel={onClose}
      cancelButtonProps={{ style: { display: 'none' } }}
      okButtonProps={{
        onClick: onClose,
        disabled: isUploadingFile || isRemovingFile,
      }}
      destroyOnHidden
    >
      <div className="AddFileModal">
        <Spin spinning={isBusy}>
          <Dragger {...uploadProps} className="AddFileModal__dragger">
            <Space direction="vertical">
              <PaperClipOutlined />
              <span>Перетащите файлы сюда или нажмите, чтобы выбрать</span>
            </Space>
          </Dragger>
        </Spin>
        <Text type="secondary" className="AddFileModal__helper">
          {HELPER_TEXT}
        </Text>

        {files.length > 0 && (
          <div className="AddFileModal__files-list">
            {files.map((file) => (
              <div key={file.Id} className="AddFileModal__file-item">
                <span
                  className="AddFileModal__file-name"
                  title={file.FileName ?? undefined}
                >
                  {file.FileName ?? 'Без названия'}
                </span>
                <Space size={4}>
                  <Tooltip title="Скачать файл">
                    <Button
                      type="text"
                      size="small"
                      icon={<DownloadOutlined />}
                      onClick={() => onDownloadFile(file)}
                      loading={isDownloadingFile}
                    />
                  </Tooltip>
                  <Tooltip title="Удалить файл">
                    <Button
                      type="text"
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => onRemoveFile(file.Id)}
                      loading={isRemovingFile}
                    />
                  </Tooltip>
                </Space>
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
};
